import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Moon, Search, Sun, UserRound, X, type LucideIcon } from 'lucide-react';
import { Avatar, Badge } from '@ui/components/ui';
import { useLiveEvents } from '@ui/hooks/useLiveEvents';
import { useTheme } from '@ui/hooks/useTheme';
import { cn } from '@ui/lib/cn';
import { useRiseIn } from '@ui/lib/motion';
import { setCommandOpen, useAppDispatch, useAppSelector } from '@ui/store';
import { useLogoutMutation, useMeQuery } from '@ui/store/authApi';
import { api } from '@ui/store/baseApi';

/**
 * The frame both apps share: sidebar, top bar, ⌘K, theme, account menu.
 * Each app passes its own navigation and the extras only it has (the
 * customer app's organization switcher and wallet; the admin panel's badge).
 */

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  keywords?: string;
}

export interface NavGroup {
  group: string;
  items: NavItem[];
}

export interface ShellProps {
  nav: NavGroup[];
  /** Small label next to the product name, e.g. "Admin". */
  badge?: ReactNode;
  /** Above the navigation, e.g. the organization switcher. */
  sidebarTop?: ReactNode;
  /** In the top bar before the theme toggle, e.g. live calls and wallet. */
  topbarExtras?: ReactNode;
  /** A strip above the top bar, e.g. "viewing as admin". */
  banner?: ReactNode;
  /** Server-Sent Events path for live refreshes. */
  liveEventsPath: string | null;
  profilePath: string;
  /** Extra account-menu entries; call close() when one is chosen. */
  menuItems?: (close: () => void) => ReactNode;
}

function Brand({ badge }: { badge?: ReactNode }) {
  const { data } = useMeQuery();
  return (
    <Link to="/" className="flex items-center gap-2 px-2">
      <img src="/favicon.svg" alt="" className="h-7 w-7" />
      <span className="text-[15px] font-semibold tracking-tight">{data?.platform.appName ?? 'Dialwise'}</span>
      {badge && <Badge tone="brand">{badge}</Badge>}
    </Link>
  );
}

function NavGroups({ groups, onNavigate }: { groups: NavGroup[]; onNavigate?: () => void }) {
  return (
    <nav className="space-y-5 px-2">
      {groups.map((g) => (
        <div key={g.group || 'root'}>
          {g.group && <div className="mb-1 px-2.5 text-2xs font-semibold uppercase tracking-wider text-muted">{g.group}</div>}
          <div className="space-y-0.5">
            {g.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) => cn('flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition', isActive ? 'bg-brand-soft text-brand-ink' : 'text-ink-2 hover:bg-raised hover:text-ink')}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function UserMenu({ profilePath, menuItems }: { profilePath: string; menuItems?: ShellProps['menuItems'] }) {
  const { data: session } = useMeQuery();
  const [open, setOpen] = useState(false);
  const [logout] = useLogoutMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  if (!session) return null;
  const name = `${session.user.firstName} ${session.user.lastName}`.trim();
  const close = () => setOpen(false);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-full p-0.5 hover:bg-raised" aria-label="Account menu">
        <Avatar name={name || session.user.email} color={session.user.avatarColor} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded-lg border border-line bg-surface p-1 shadow-pop">
          <div className="border-b border-line px-3 py-2.5">
            <div className="truncate text-sm font-medium">{name}</div>
            <div className="truncate text-xs text-muted">{session.user.email}</div>
          </div>
          <Link to={profilePath} onClick={close} className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-raised">
            <UserRound className="h-4 w-4 text-muted" /> Profile & password
          </Link>
          {menuItems?.(close)}
          <button
            onClick={async () => {
              await logout().unwrap().catch(() => undefined);
              dispatch(api.util.resetApiState());
              navigate('/login');
            }}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-bad hover:bg-bad-soft"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Styled entry for ShellProps.menuItems. */
export function MenuItem({ icon: Icon, children, onClick }: { icon: LucideIcon; children: ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-raised">
      <Icon className="h-4 w-4 text-muted" /> {children}
    </button>
  );
}

function CommandPalette({ groups }: { groups: NavGroup[] }) {
  const open = useAppSelector((s) => s.ui.commandOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [index, setIndex] = useState(0);
  const items = useMemo(() => {
    const all = groups.flatMap((g) => g.items);
    const term = q.toLowerCase().trim();
    return term ? all.filter((i) => `${i.label} ${i.keywords ?? ''}`.toLowerCase().includes(term)) : all;
  }, [groups, q]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        dispatch(setCommandOpen(!open));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dispatch]);
  useEffect(() => {
    setQ('');
    setIndex(0);
  }, [open]);
  if (!open) return null;
  const go = (item?: NavItem) => {
    if (!item) return;
    dispatch(setCommandOpen(false));
    navigate(item.to);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[14vh]">
      <div className="fixed inset-0 bg-slate-950/40" onClick={() => dispatch(setCommandOpen(false))} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-line bg-surface shadow-pop">
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search className="h-4 w-4 text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') setIndex((i) => Math.min(i + 1, items.length - 1));
              if (e.key === 'ArrowUp') setIndex((i) => Math.max(i - 1, 0));
              if (e.key === 'Enter') go(items[index]);
              if (e.key === 'Escape') dispatch(setCommandOpen(false));
            }}
            placeholder="Jump to…"
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
          />
          <span className="kbd">esc</span>
        </div>
        <div className="max-h-80 overflow-y-auto p-1.5">
          {items.map((item, i) => (
            <button key={item.to} onMouseEnter={() => setIndex(i)} onClick={() => go(item)} className={cn('flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm', i === index ? 'bg-brand-soft text-brand-ink' : 'text-ink-2')}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          ))}
          {!items.length && <p className="px-3 py-6 text-center text-sm text-muted">No matches</p>}
        </div>
      </div>
    </div>
  );
}

export function AppShell({ nav, badge, sidebarTop, topbarExtras, banner, liveEventsPath, profilePath, menuItems }: ShellProps) {
  const [theme, toggleTheme] = useTheme();
  const [mobileNav, setMobileNav] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  useLiveEvents(liveEventsPath);
  const main = useRef<HTMLDivElement>(null);
  useRiseIn(main, [location.pathname]);
  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  const sidebar = (onNavigate?: () => void): ReactNode => (
    <div className="flex h-full flex-col gap-4 py-4">
      <Brand badge={badge} />
      {sidebarTop}
      <div className="flex-1 overflow-y-auto">
        <NavGroups groups={nav} onNavigate={onNavigate} />
      </div>
      <div className="px-4 text-2xs text-muted">
        <span className="kbd">⌘</span> <span className="kbd">K</span> to jump anywhere
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-60 border-r border-line bg-page lg:block">{sidebar()}</aside>
      {mobileNav && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setMobileNav(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-line bg-page">
            <button className="absolute right-3 top-4 text-muted" onClick={() => setMobileNav(false)} aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
            {sidebar(() => setMobileNav(false))}
          </aside>
        </div>
      )}
      <div className="lg:pl-60">
        {banner}
        <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-line bg-page/85 px-4 backdrop-blur sm:px-6">
          <button className="text-ink-2 lg:hidden" onClick={() => setMobileNav(true)} aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <button onClick={() => dispatch(setCommandOpen(true))} className="flex h-8 w-full max-w-sm items-center gap-2 rounded-md border border-line bg-surface px-3 text-sm text-muted shadow-sm hover:border-line-strong">
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search or jump to…</span>
            <span className="kbd hidden sm:inline-flex">⌘K</span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            {topbarExtras}
            <button onClick={toggleTheme} className="rounded-md p-2 text-ink-2 hover:bg-raised" aria-label="Toggle theme">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <UserMenu profilePath={profilePath} menuItems={menuItems} />
          </div>
        </header>
        <main ref={main} className="mx-auto w-full max-w-[1280px] px-4 py-6 sm:px-6 lg:py-8">
          <Outlet />
        </main>
      </div>
      <CommandPalette groups={nav} />
    </div>
  );
}
